import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import {
  errorMessageForContactName,
  errorMessageForPhoneNumber,
} from "@/Shared/Contacts/Validators/contactsErrorMessagesForContact";
import type { EmergencyContactDetails } from "@freightsimple/generated-dashboard-openapi-client/models/EmergencyContactDetails";
import { isValidErapPlanNumber } from "../Helpers/dangerousGoodsErapPlanNumber";
import type { ShipmentLimits } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentLimits";

export interface EmergencyContactDetailsErrors {
  contactName: ErrorMessageType;
  contactPhoneNumber: ErrorMessageType;
  erapPlanNumber: ErrorMessageType;
  erapPhoneNumber: ErrorMessageType;
  tdgResponsibleName: ErrorMessageType;
}

const noErrors: EmergencyContactDetailsErrors = {
  contactName: undefined,
  contactPhoneNumber: undefined,
  erapPlanNumber: undefined,
  erapPhoneNumber: undefined,
  tdgResponsibleName: undefined,
};

function isFilled(value: string | undefined): value is string {
  return value !== undefined && value !== "";
}

/**
 * `showCanadianTdgFields` is true when the shipment touches Canada: only then
 * is the TDG-responsible name required. ERAP is validated whenever present.
 * The bounds come from the served shipment limits, so the numbers the UI
 * enforces are the ones the backend enforces.
 */
export function dangerousGoodsErrorMessagesForEmergencyContact(
  emergencyContactDetails: Partial<EmergencyContactDetails> | undefined,
  showCanadianTdgFields: boolean,
  limits: ShipmentLimits,
): EmergencyContactDetailsErrors {
  if (emergencyContactDetails === undefined) {
    return noErrors;
  }

  const {
    contactName,
    contactPhoneNumber,
    erapPlanNumber,
    erapPhoneNumber,
    tdgResponsibleName,
  } = emergencyContactDetails;

  // ERAP is optional, but if one field is filled the other becomes required.
  const erapPlanFilled = isFilled(erapPlanNumber);
  const erapPhoneFilled = isFilled(erapPhoneNumber);
  const erapAnyFilled = erapPlanFilled || erapPhoneFilled;

  function erapPlanError(): ErrorMessageType {
    if (erapAnyFilled && !erapPlanFilled) {
      return "Required";
    }
    if (
      erapPlanFilled &&
      !isValidErapPlanNumber(
        erapPlanNumber,
        limits.dangerousGoods.erapPlanNumberPattern,
      )
    ) {
      return "Enter a valid ERAP plan number (e.g. 2-1234-567)";
    }
    return undefined;
  }

  function erapPhoneError(): ErrorMessageType {
    if (!erapAnyFilled) {
      return undefined;
    }
    return erapPhoneFilled
      ? errorMessageForPhoneNumber(erapPhoneNumber)
      : "Required";
  }

  function tdgResponsibleError(): ErrorMessageType {
    if (!showCanadianTdgFields) {
      return undefined;
    }
    if (!isFilled(tdgResponsibleName)) {
      return "Required";
    }
    // It is a person, so require a first and last name.
    if (tdgResponsibleName.trim().split(/\s+/).length < 2) {
      return "Enter a first and last name";
    }
    const maxLength = limits.dangerousGoods.maxTdgResponsibleNameLength;
    if (tdgResponsibleName.length > maxLength) {
      return `Must be ${maxLength} characters or fewer`;
    }
    return undefined;
  }

  return {
    contactName: errorMessageForContactName(contactName),
    contactPhoneNumber: errorMessageForPhoneNumber(contactPhoneNumber),
    erapPlanNumber: erapPlanError(),
    erapPhoneNumber: erapPhoneError(),
    tdgResponsibleName: tdgResponsibleError(),
  };
}
